import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  room_type_code: '',
  room_type_name: '',
  total_rooms: '0',
  max_adults: '2',
  max_children: '0',
  max_infants: '0',
  max_pax: '2',
  tax_id: '',
  is_tax_exempt: false,
  status: 'ACTIVE',
  is_active: true
};

function PmsRoomTypeMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [taxRows, setTaxRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async (preserveMessage = false) => {
    if (!selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/pms/room-types?all=1', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load room types');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!preserveMessage) setError(err?.message || 'Failed to load room types');
    }
  };

  const loadTaxRows = async () => {
    if (!selectedProperty?.id) return setTaxRows([]);
    try {
      const res = await fetch('/api/tax-master', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load tax profiles');
      setTaxRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load tax profiles');
    }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    loadRows();
    loadTaxRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const sanitizeIntegerText = (value) => String(value || '').replace(/[^\d]/g, '');
  const parseIntegerOrDefault = (value, fallback) => {
    const raw = String(value || '').trim();
    if (!raw) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return Number.NaN;
    return Math.trunc(parsed);
  };

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    setMessage('');
    if (!String(form.room_type_code || '').trim()) return setError('Room Type Code is required');
    if (!String(form.room_type_name || '').trim()) return setError('Room Type Name is required');
    const maxAdults = parseIntegerOrDefault(form.max_adults, 2);
    const maxChildren = parseIntegerOrDefault(form.max_children, 0);
    const maxInfants = parseIntegerOrDefault(form.max_infants, 0);
    // Auto-calculate max_pax if not set or set to 0
    const rawPax = parseIntegerOrDefault(form.max_pax, 0);
    const maxPax = rawPax > 0 ? rawPax : (maxAdults + maxChildren);
    const totalRooms = parseIntegerOrDefault(form.total_rooms, 0);
    if ([maxAdults, maxChildren, maxInfants, maxPax, totalRooms].some((n) => Number.isNaN(n))) {
      return setError('All max fields and default room count must be whole numbers');
    }
    if (maxAdults < 1) return setError('Max Adults must be at least 1');
    if (maxChildren < 0) return setError('Max Children cannot be negative');
    if (maxInfants < 0) return setError('Max Infant cannot be negative');
    if (maxPax < 1) return setError('Total Occupancy must be at least 1');
    if (maxAdults > maxPax) return setError(`Max Adults (${maxAdults}) cannot exceed Total Occupancy (${maxPax})`);
    if (maxChildren > maxPax) return setError(`Max Children (${maxChildren}) cannot exceed Total Occupancy (${maxPax})`);
    if ((maxAdults + maxChildren) > maxPax) return setError(`Combined Adults + Children (${maxAdults + maxChildren}) cannot exceed Total Occupancy (${maxPax})`);
    if (maxPax > (maxAdults + maxChildren)) return setError('Total Occupancy cannot exceed Max Adults + Max Children');
    const selectedTaxRow = taxRows.find((row) => String(row.id) === String(form.tax_id));

    setError('');
    try {
      const url = editingId ? `/api/v1/pms/room-types/${editingId}` : '/api/v1/pms/room-types';
      const method = editingId ? 'PUT' : 'POST';
      const payload = {
        ...form,
        total_rooms: totalRooms,
        max_adults: maxAdults,
        max_children: maxChildren,
        max_infants: maxInfants,
        max_pax: maxPax,
        tax_regime: selectedTaxRow ? String(selectedTaxRow.tax_regime || 'NONE').toUpperCase() : 'NONE',
        tax_id: form.tax_id ? Number(form.tax_id) : null,
        is_tax_exempt: form.is_tax_exempt === true
      };
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      const label = editingId ? `Room type "${form.room_type_name}" updated successfully` : `Room type "${form.room_type_name}" created successfully`;
      setForm(emptyForm);
      setEditingId('');
      setMessage(label);
      loadRows(true);
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this room type?')) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/room-types/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setMessage('Room type deactivated successfully');
      loadRows(true);
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const editRow = (row) => {
    setEditingId(String(row.room_type_id));
    setForm({
      room_type_code: row.room_type_code || '',
      room_type_name: row.room_type_name || '',
      total_rooms: String(Number(row.total_rooms || 0)),
      max_adults: String(Number(row.max_adults || 2)),
      max_children: String(Number(row.max_children || 0)),
      max_infants: String(Number(row.max_infants || 0)),
      max_pax: String(Number(row.max_pax || 2)),
      tax_id: row.tax_id ? String(row.tax_id) : '',
      is_tax_exempt: row.is_tax_exempt === true,
      status: row.status || 'ACTIVE',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Room Type Master</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.5rem 1rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.875rem' }}>{message}</div> : null}

      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#334155' }}>
        Define each room type with capacity and inventory defaults. Fields marked <span style={{ color: '#b91c1c' }}>*</span> are mandatory.
      </div>

      <div ref={formRef} className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.6rem', alignItems: 'start', ...editHighlightStyle(editHighlight) }}>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Type Code <span style={{ color: '#b91c1c' }}>*</span></span>
          <input
            placeholder="Ex: DLX / SUITE / STD"
            value={form.room_type_code}
            onChange={(e) => setForm({ ...form, room_type_code: e.target.value })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Unique code for reports.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Room Type Name <span style={{ color: '#b91c1c' }}>*</span></span>
          <input
            placeholder="Ex: Deluxe Double / Executive Suite"
            value={form.room_type_name}
            onChange={(e) => setForm({ ...form, room_type_name: e.target.value })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Name shown in reservation forms.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Default Room Count</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            value={form.total_rooms}
            onChange={(e) => setForm({ ...form, total_rooms: sanitizeIntegerText(e.target.value) })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Planning count for this type.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Max Adults</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="2"
            value={form.max_adults}
            onChange={(e) => setForm({ ...form, max_adults: sanitizeIntegerText(e.target.value) })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Max adults per room.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Max Children</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            value={form.max_children}
            onChange={(e) => setForm({ ...form, max_children: sanitizeIntegerText(e.target.value) })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Max children per room.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Max Infant</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="0"
            value={form.max_infants}
            onChange={(e) => setForm({ ...form, max_infants: sanitizeIntegerText(e.target.value) })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Max infants per room.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Total Occupancy</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Auto (Adults + Children)"
            value={form.max_pax}
            onChange={(e) => setForm({ ...form, max_pax: sanitizeIntegerText(e.target.value) })}
          />
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Auto: Adults + Children ({Number(form.max_adults || 0) + Number(form.max_children || 0)}).</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Business Status</span>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Open for new reservations.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Tax</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minHeight: 32 }}>
            <input
              type="checkbox"
              checked={form.is_tax_exempt === true}
              onChange={(e) => setForm({ ...form, is_tax_exempt: e.target.checked })}
            />
            <span style={{ fontSize: '0.85rem' }}>No Tax (exempt)</span>
          </span>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Bills for this room type are posted with zero tax, overriding Hotel Room Tax Policy.</span>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Record Active Flag</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </span>
          <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>Uncheck to hide from lists.</span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', paddingTop: '1.4rem' }}>
          <button onClick={save} style={{ padding: '0.45rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', width: 'auto' }}>{editingId ? 'Update' : 'Create'}</button>
          <button onClick={() => { setEditingId(''); setForm(emptyForm); setError(''); setMessage(''); }} style={{ padding: '0.45rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', width: 'auto' }}>Clear</button>
        </div>
      </div>

      <div style={{ marginTop: '0.8rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Rooms</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Capacity</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.room_type_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ padding: '0.45rem' }}>{r.room_type_code}</td>
                <td style={{ padding: '0.45rem' }}>{r.room_type_name}</td>
                <td style={{ padding: '0.45rem' }}>{r.total_rooms}</td>
                <td style={{ padding: '0.45rem' }}>
                  A:{r.max_adults} C:{r.max_children} I:{r.max_infants || 0} | Total Occupancy:{Number(r.max_pax) > 0 ? r.max_pax : (Number(r.max_adults || 0) + Number(r.max_children || 0))}
                </td>
                <td style={{ padding: '0.45rem' }}>{r.is_active ? 'Active' : 'Inactive'}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem' }}>
                  <button onClick={() => editRow(r)}>Edit</button>
                  {r.is_active ? <button onClick={() => remove(r.room_type_id)}>Deactivate</button> : null}
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td style={{ padding: '0.6rem' }} colSpan={7}>No room types found</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsRoomTypeMaster;
